import axios from 'axios';
import i18n from '../i18n';
import { prepareDocumentUploadFile } from '../utils/imageUploadCompression';

/** Rejection shaped like axios error so upload callers surface `detail` consistently. */
function rejectUploadPrepare(message) {
  const err = new Error(message);
  err.response = { data: { detail: message } };
  return Promise.reject(err);
}

/**
 * True when REACT_APP_API_URL points at a backend on this machine (direct calls, no CORS issue).
 */
function isLocalBackendUrl(url) {
  if (!url || !String(url).trim()) return false;
  try {
    const u = new URL(url);
    return u.hostname === 'localhost' || u.hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

/**
 * Backend API base URL.
 * - Dev + remote API (e.g. https://dev.globalmobility.law/api): use `/api` so CRA `setupProxy.js`
 *   forwards to the real host - browser stays same-origin and avoids CORS.
 * - Dev + local API: `REACT_APP_API_URL=http://localhost:8000/api` uses that URL directly.
 * - Production: `REACT_APP_API_URL` or same-host `/api`.
 */
function getApiBaseUrl() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  const isDev = typeof process !== 'undefined' && process.env.NODE_ENV === 'development';

  if (isDev && envUrl && !isLocalBackendUrl(envUrl)) {
    return '/api';
  }
  if (envUrl) return envUrl;
  if (isDev) return '/api';
  return '/api';
}

/**
 * Origin of the backend (OAuth redirects must hit the real API, not localhost:3000).
 * Order: REACT_APP_API_URL → REACT_APP_PROXY_TARGET → dev default → window (prod same-site).
 */
function getBackendOrigin() {
  const envUrl = (process.env.REACT_APP_API_URL || '').trim();
  if (envUrl.startsWith('http://') || envUrl.startsWith('https://')) {
    try {
      return new URL(envUrl).origin;
    } catch {
      // fall through
    }
  }
  const proxyTarget = (process.env.REACT_APP_PROXY_TARGET || '').trim();
  if (proxyTarget.startsWith('http')) {
    try {
      return new URL(proxyTarget).origin;
    } catch {
      // fall through
    }
  }
  if (process.env.NODE_ENV === 'development') {
    return 'https://dev.globalmobility.law';
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return '';
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
});

const API_DETAIL_TRANSLATIONS_ES = {
  'Authentication required': 'Autenticacion requerida',
  'Invalid token': 'Token invalido',
  'Admin access required': 'Se requiere acceso de administrador',
  'Internal server error': 'Error interno del servidor',
  'User not found': 'Usuario no encontrado',
  'Session not found': 'Sesion no encontrada',
  'Case not found': 'Caso no encontrado',
  'Document not found': 'Documento no encontrado',
  'Invalid case reference': 'Referencia de caso invalida',
  'Invalid document id': 'ID de documento invalido',
  'Invalid status': 'Estado invalido',
  'Message is required': 'El mensaje es obligatorio',
  'Field name is required': 'El nombre del campo es obligatorio',
  'Text is required': 'El texto es obligatorio',
  'No file selected': 'No se selecciono ningun archivo',
  'File not found in storage': 'Archivo no encontrado en el almacenamiento',
  'Could not read file from storage': 'No se pudo leer el archivo del almacenamiento',
  'Document storage unavailable': 'El almacenamiento de documentos no esta disponible',
  'File is not available for download (not stored in S3).': 'El archivo no esta disponible para descargar (no esta almacenado en S3).',
  'File is not available (not stored in S3).': 'El archivo no esta disponible (no esta almacenado en S3).',
  'Invalid or expired code': 'Codigo invalido o vencido',
  'Google Sign-In is not configured': 'Google Sign-In no esta configurado',
  'Email is required.': 'El correo electronico es obligatorio.',
  'This email is already registered.': 'Este correo electronico ya esta registrado.',
  'This phone number is already registered.': 'Este numero de telefono ya esta registrado.',
};

function currentLanguage() {
  const raw = (i18n.language || 'en').split('-')[0].toLowerCase();
  return raw === 'en' ? 'en' : 'es';
}

function localizeApiDetail(detail) {
  const lang = currentLanguage();
  if (lang !== 'es' || detail == null) return detail;
  if (typeof detail === 'string') return API_DETAIL_TRANSLATIONS_ES[detail] || detail;
  if (Array.isArray(detail)) return detail.map((item) => localizeApiDetail(item));
  if (typeof detail === 'object') {
    const next = { ...detail };
    if (typeof next.msg === 'string') next.msg = API_DETAIL_TRANSLATIONS_ES[next.msg] || next.msg;
    return next;
  }
  return detail;
}

const SITUATION_SUB_ROUTES = new Set(['work', 'family', 'vulnerability']);
const SITUATION_SOURCE_SUB_ROUTES = new Set(['7A', '7B', '7C', '8', '9', '10', '11', '12']);
const SITUATION_PRORROGA_TYPES = new Set(['jobSearch', 'integrationReport', 'illness', 'disability', 'retirement']);

function legacyRouteFromSourceSubRoute(raw) {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value === '7A') return 'work';
  if (value === '7B') return 'family';
  if (value === '7C') return 'vulnerability';
  return null;
}

/**
 * Keep the situation branch payload self-consistent before it leaves the browser.
 * This is used by both the step submit endpoint (snake_case) and autosave (camelCase)
 * so Mongo never keeps stale asylum-only / irregular-only values when the answer flips.
 */
function normalizeSituationBranchFields(details) {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return details;

  const next = { ...details };

  const normalizeWorkHistory = (raw) => {
    const value = typeof raw === 'string' ? raw.trim().toLowerCase() : '';
    return ['worked', 'current', 'offer'].includes(value) ? value : null;
  };

  if (Object.prototype.hasOwnProperty.call(next, 'asylum_applicant')) {
    if (next.asylum_applicant === true) {
      const oar = typeof next.oar_case_number === 'string' ? next.oar_case_number.trim() : '';
      next.oar_case_number = oar || null;
      next.asylum_scenario =
        typeof next.asylum_scenario === 'string' && ['1', '2', '3', '4', '5', '6'].includes(next.asylum_scenario)
          ? next.asylum_scenario
          : null;
      next.prorroga_type =
        typeof next.prorroga_type === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorroga_type)
          ? next.prorroga_type
          : null;
      next.sub_route = null;
      next.irregular_sub_route = null;
      next.work_history = null;
      next.work_type = null;
      next.self_employment_activity = null;
      next.qualifying_ground = null;
    } else if (next.asylum_applicant === false) {
      const subRoute = typeof next.sub_route === 'string' ? next.sub_route.trim() : '';
      next.sub_route = SITUATION_SOURCE_SUB_ROUTES.has(subRoute) ? subRoute : null;
      const legacyRoute =
        typeof next.irregular_sub_route === 'string' ? next.irregular_sub_route.trim().toLowerCase() : '';
      next.irregular_sub_route = SITUATION_SUB_ROUTES.has(legacyRoute)
        ? legacyRoute
        : legacyRouteFromSourceSubRoute(next.sub_route);
      next.oar_case_number = null;
      next.asylum_scenario = null;
      next.prorroga_type =
        typeof next.prorroga_type === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorroga_type)
          ? next.prorroga_type
          : null;
      next.work_history = next.irregular_sub_route === 'work' ? normalizeWorkHistory(next.work_history) : null;
      next.work_type =
        next.sub_route === '7A' && ['employed', 'selfemployed'].includes(String(next.work_type ?? '').trim())
          ? String(next.work_type).trim()
          : null;
      next.self_employment_activity =
        next.sub_route === '7A' && typeof next.self_employment_activity === 'string'
          ? next.self_employment_activity.trim() || null
          : null;
    }
  }

  if (Object.prototype.hasOwnProperty.call(next, 'asylumApplicant')) {
    const asylum = String(next.asylumApplicant ?? '').trim().toLowerCase();
    if (asylum === 'yes') {
      next.oarCaseNumber = typeof next.oarCaseNumber === 'string' ? next.oarCaseNumber.trim() : '';
      next.asylumScenario =
        typeof next.asylumScenario === 'string' && ['1', '2', '3', '4', '5', '6'].includes(next.asylumScenario)
          ? next.asylumScenario
          : '';
      next.prorrogaType =
        typeof next.prorrogaType === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorrogaType)
          ? next.prorrogaType
          : '';
      next.subRoute = '';
      next.irregularSubRoute = '';
      next.workHistory = '';
      next.workType = '';
      next.selfEmploymentActivity = '';
      next.qualifyingGround = '';
    } else if (asylum === 'no') {
      const subRoute = typeof next.subRoute === 'string' ? next.subRoute.trim() : '';
      next.subRoute = SITUATION_SOURCE_SUB_ROUTES.has(subRoute) ? subRoute : '';
      const route =
        typeof next.irregularSubRoute === 'string' ? next.irregularSubRoute.trim().toLowerCase() : '';
      next.irregularSubRoute = SITUATION_SUB_ROUTES.has(route)
        ? route
        : legacyRouteFromSourceSubRoute(next.subRoute) || '';
      next.oarCaseNumber = '';
      next.asylumScenario = '';
      next.prorrogaType =
        typeof next.prorrogaType === 'string' && SITUATION_PRORROGA_TYPES.has(next.prorrogaType)
          ? next.prorrogaType
          : '';
      next.workHistory = next.irregularSubRoute === 'work' ? (normalizeWorkHistory(next.workHistory) || '') : '';
      next.workType =
        next.subRoute === '7A' && ['employed', 'selfemployed'].includes(String(next.workType ?? '').trim())
          ? String(next.workType).trim()
          : '';
      next.selfEmploymentActivity =
        next.subRoute === '7A' && typeof next.selfEmploymentActivity === 'string'
          ? next.selfEmploymentActivity.trim()
          : '';
    } else {
      next.oarCaseNumber = '';
      next.asylumScenario = '';
      next.subRoute = '';
      next.prorrogaType = '';
      next.irregularSubRoute = '';
      next.workHistory = '';
      next.workType = '';
      next.selfEmploymentActivity = '';
    }
  }

  return next;
}

api.interceptors.request.use((config) => {
  const lng = currentLanguage();
  config.headers['Accept-Language'] = lng;
  // Public signup/landing calls must not send a stale admin/applicant Bearer token.
  if (config.public !== true) {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.data && Object.prototype.hasOwnProperty.call(err.response.data, 'detail')) {
      err.response.data.detail = localizeApiDetail(err.response.data.detail);
    }
    if (err.response?.status === 401) {
      try {
        const path = typeof window !== 'undefined' ? (window.location?.pathname || '') : '';
        const search = typeof window !== 'undefined' ? (window.location?.search || '') : '';
        const isDevBypass =
          process.env.NODE_ENV !== 'production'
          && new URLSearchParams(search).get('bypass') === '1';
        // OAuth handoff: AuthCallback just stored a fresh JWT - do not wipe it or
        // hard-redirect away when /auth/me fails transiently (or races).
        const isOAuthCallback = path === '/auth/callback' || path.endsWith('/auth/callback');
        const isLoginPath = path === '/login' || path === '/es/login';
        if (!isOAuthCallback && !isDevBypass) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('immigrationSessionId');
          if (!isLoginPath && typeof window !== 'undefined') {
            window.location.assign('/login');
          }
        }
      } catch {
        // ignore
      }
    }
    return Promise.reject(err);
  }
);

/** Full API: auth, session, chat, data, livechat, admin (same surface as OG frontend). */
export const mvpApi = {
  // --- Auth (unchanged) ---
  getPartnerByToken: (token) =>
    api.get(`/partners/by-token/${encodeURIComponent(String(token).trim())}`, { public: true }),
  /** Public: validate a customer magic-link token and return the org name. */
  getOrgByCustomerRef: (ref) =>
    api.get(`/org-auth/by-customer-ref/${encodeURIComponent(String(ref).trim())}`, { public: true }),
  /** Public: validate a personalized crew invite token (with optional org ref). */
  getCrewInvite: (token, orgRef = null) =>
    api.get(`/org-auth/crew-invite/${encodeURIComponent(String(token).trim())}`, {
      params: orgRef ? { org: String(orgRef).trim() } : undefined,
      public: true,
    }),
  /** Public: active users registered for this E.164 phone (sales funnel signup check). */
  getUsersByPhone: (phone) =>
    api.get('/auth/users/by-phone', { params: { phone }, public: true }),
  requestPhoneOtp: (phone, channel = 'sms', intent = 'signup', customerOrgRef = null, crewInviteToken = null) =>
    api.post(
      '/auth/phone/request-otp',
      {
        phone,
        channel,
        intent,
        customer_org_ref: customerOrgRef || undefined,
        crew_invite_token: crewInviteToken || undefined,
      },
      { public: true },
    ),
  verifyPhoneOtp: (phone, code, intent = 'signup', referralCode = null, partnerToken = null, browserLanguage = null, customerOrgRef = null, crewInviteToken = null) =>
    api.post(
      '/auth/phone/verify-otp',
      {
        phone,
        code,
        intent,
        referral_code: referralCode || undefined,
        partner_token: partnerToken || undefined,
        browser_language: browserLanguage || undefined,
        customer_org_ref: customerOrgRef || undefined,
        crew_invite_token: crewInviteToken || undefined,
      },
      { public: true },
    ),
  /** List productions for a participant email (multi-project picker). */
  listParticipantProjects: (email, intent = 'signup') =>
    api.post(
      '/auth/email/projects',
      { email, intent },
      { public: true },
    ),

  /** List productions for a participant phone (multi-project picker on sign-in). */
  listParticipantPhoneProjects: (phone, intent = 'login') =>
    api.post(
      '/auth/phone/projects',
      { phone, intent },
      { public: true },
    ),

  /** Participant invite signup: email must match crew CSV invite; sends GM-branded OTP. */
  requestParticipantEmailOtp: (email, customerOrgRef, crewInviteToken, intent = 'signup', channel = 'email') =>
    api.post(
      '/auth/email/request-otp',
      {
        email,
        intent,
        customer_org_ref: customerOrgRef || undefined,
        crew_invite_token: crewInviteToken || undefined,
        channel: channel || 'email',
      },
      { public: true },
    ),
  /** After email entry: which OTP channels are available (email always; sms if phone on file). */
  getParticipantOtpChannels: (email, customerOrgRef, crewInviteToken) =>
    api.post(
      '/auth/email/otp-channels',
      {
        email,
        customer_org_ref: customerOrgRef || undefined,
        crew_invite_token: crewInviteToken || undefined,
      },
      { public: true },
    ),
  /** Verify participant email/SMS OTP and issue JWT (same response shape as phone verify). */
  verifyParticipantEmailOtp: (email, code, customerOrgRef, crewInviteToken, intent = 'signup', browserLanguage = null, channel = 'email') =>
    api.post(
      '/auth/email/verify-otp',
      {
        email,
        code,
        intent,
        customer_org_ref: customerOrgRef || undefined,
        crew_invite_token: crewInviteToken || undefined,
        browser_language: browserLanguage || undefined,
        channel: channel || 'email',
      },
      { public: true },
    ),
  getMe: () => api.get('/auth/me'),
  /** OAuth callback: send Bearer explicitly (avoid interceptor/localStorage races). */
  getMeWithToken: (token) =>
    api.get('/auth/me', {
      headers: { Authorization: `Bearer ${String(token || '').trim()}` },
    }),
  /**
   * Exchange short-lived OAuth httpOnly cookie (set by Google/Apple callback)
   * for a session JWT. Must not send a stale Bearer token.
   */
  consumeOAuthHandoff: () =>
    api.post('/auth/oauth/consume', null, { public: true }),
  /** Finish SSO login after choosing a production (multi-account email). */
  selectOAuthProject: (pickToken, projectId) =>
    api.post(
      '/auth/oauth/select-project',
      { pick_token: pickToken, project_id: projectId },
      { public: true },
    ),
  updatePersonalDetails: (details) => api.put('/auth/personal-details', details),
  updateEligibilityRouting: (details) =>
    api.put('/auth/eligibility-routing', normalizeSituationBranchFields(details)),
  updateSituationDetails: (details) =>
    api.put('/auth/situation-details', normalizeSituationBranchFields(details)),
  /**
   * Partial autosave (Mongo personal_data). Address keys match Address step UI:
   * street→domicilio, streetNumber→numero_domicilio, floorPiso→piso, floorDoor→puerta,
   * city→localidad, province→provincia, postalCode→codigo_postal, country→pais_residencia,
   * residenceSince→residente_desde (send {month,year}; empty both clears when key present).
   */
  applicantAutosave: (details) =>
    api.put('/auth/applicant-autosave', normalizeSituationBranchFields(details)),
  getProcedureIntake: (procedureSlug) =>
    api.get(`/auth/procedure-intake/${encodeURIComponent(procedureSlug)}`),
  autosaveProcedureIntake: (procedureSlug, values, opts = {}) =>
    api.put(`/auth/procedure-intake/${encodeURIComponent(procedureSlug)}`, {
      values,
      ...(opts.sectionCompleted ? { section_completed: opts.sectionCompleted } : {}),
    }),
  /**
   * Scrape public profile links, parse uploaded career docs, and persist a
   * synthesized professional profile to Mongo personal_data.
   */
  synthesizeProfessionalProfile: ({
    procedureSlug,
    purpose = 'profile',
    notes = '',
    links = [],
    files = [],
    existingFileNames = [],
  }) => {
    const formData = new FormData();
    formData.append('procedure_slug', procedureSlug || '');
    formData.append('purpose', purpose || 'profile');
    formData.append('notes', notes || '');
    formData.append('links', JSON.stringify(Array.isArray(links) ? links : []));
    formData.append(
      'existing_file_names',
      JSON.stringify(Array.isArray(existingFileNames) ? existingFileNames : []),
    );
    (files || []).forEach((file) => {
      if (file) formData.append('files', file);
    });
    return api.post('/auth/professional-profile/synthesize', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },

  /** Participant Background details (place of birth, occupation, prior work). */
  getBackgroundDetails: () => api.get('/auth/background-details'),
  createBackgroundDetails: (details) => api.post('/auth/background-details', details),
  updateBackgroundDetails: (details) => api.put('/auth/background-details', details),

  /** Participant Personal details (gm-content collection_profile concepts). */
  getPersonalCollection: () => api.get('/auth/personal-collection'),
  savePersonalCollection: (details) => api.put('/auth/personal-collection', details),

  /** Personal details on the Mongo user profile. Keys are English. */
  getUserProfilePersonalDetails: () => api.get('/auth/user-profile/personal-details'),
  saveUserProfilePersonalDetails: (details) => api.post('/auth/user-profile/personal-details', details),
  getUserProfileSocialMedia: () => api.get('/auth/user-profile/social-media'),
  saveUserProfileSocialMedia: (details) => api.post('/auth/user-profile/social-media', details),
  getUserProfileTravelDocuments: () => api.get('/auth/user-profile/travel-documents'),
  saveUserProfileTravelDocuments: (details) => api.post('/auth/user-profile/travel-documents', details),
  getUserProfileProductionWork: () => api.get('/auth/user-profile/production-work'),
  saveUserProfileProductionWork: (details) => api.post('/auth/user-profile/production-work', details),
  getUserProfileAccompanyingFamily: () => api.get('/auth/user-profile/accompanying-family'),
  saveUserProfileAccompanyingFamily: (details) => api.post('/auth/user-profile/accompanying-family', details),
  getUserProfileImmigrationHistory: () => api.get('/auth/user-profile/immigration-history'),
  saveUserProfileImmigrationHistory: (details) => api.post('/auth/user-profile/immigration-history', details),
  getUserProfileLegalHistory: () => api.get('/auth/user-profile/legal-history'),
  saveUserProfileLegalHistory: (details) => api.post('/auth/user-profile/legal-history', details),
  scanUserProfilePassport: async (file) => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    return api.post('/auth/user-profile/passport/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  getUserProfilePassport: () => api.get('/auth/user-profile/passport'),
  saveUserProfilePassport: (details) => api.post('/auth/user-profile/passport', details),
  getUserProfilePassportStatus: () => api.get('/auth/user-profile/passport/status'),
  saveUserProfilePassportStatus: (status) => api.post('/auth/user-profile/passport/status', status),
  getUserProfileDocuments: () => api.get('/auth/user-profile/documents'),
  saveUserProfileDocument: async (documentId, file) => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('document_id', documentId);
    formData.append('file', prep.file);
    return api.post('/auth/user-profile/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  getUserProfileResumes: () => api.get('/auth/user-profile/resumes'),
  uploadUserProfileResume: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/user-profile/resumes', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  deleteUserProfileResume: (resumeId) => api.delete(`/auth/user-profile/resumes/${encodeURIComponent(resumeId)}`),
  getUserProfileExperienceText: () => api.get('/auth/user-profile/professional-experience'),
  saveUserProfileExperienceText: (text) => api.put('/auth/user-profile/professional-experience', { text }),
  getUserProfileXAccounts: () => api.get('/auth/user-profile/x-account'),
  /** Fetches the X account's public profile + posts server-side and stores the JSON. Slow: two upstream calls. */
  saveUserProfileXAccount: (handle) => api.post('/auth/user-profile/x-account', { handle }, { timeout: 90000 }),
  /** Verify a username or profile URL via Scrape Creators; stores found JSON in Mongo. */
  verifyUserProfileOnlineAccount: ({ platform, account }) =>
    api.post('/auth/user-profile/online-account', { platform, account }, { timeout: 90000 }),
  getUserProfileProfessionalProfile: () => api.get('/auth/user-profile/professional-profile'),
  saveUserProfileProfessionalProfile: (details) => api.post('/auth/user-profile/professional-profile', details),

  /** Participant Social security certificate answers. */
  getSocialSecurityDetails: () => api.get('/auth/social-security-details'),
  saveSocialSecurityDetails: (details) => api.post('/auth/social-security-details', details),

  /** Participant Emergency contact (name, phone, relationship). */
  getEmergencyContact: () => api.get('/auth/emergency-contact'),
  saveEmergencyContact: (details) => api.post('/auth/emergency-contact', details),

  /** Participant Safety acknowledgment. */
  getSafetyAcknowledgment: () => api.get('/auth/safety-acknowledgment'),
  saveSafetyAcknowledgment: (details) => api.post('/auth/safety-acknowledgment', details),

  /** Participant Travel itinerary (CSV upload). */
  getTravelItinerary: () => api.get('/auth/travel-itinerary'),
  uploadTravelItinerary: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/travel-itinerary', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },

  markApplicantCaseNeedsAttention: (sessionId) =>
    api.post(`/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/mark-needs-attention`),
  acknowledgeDocumentSection: (sessionId, section) =>
    api.post(
      `/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/document-section-acknowledged`,
      { section }
    ),
  unacknowledgeDocumentSection: (sessionId, section) =>
    api.post(
      `/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/document-section-unacknowledged`,
      { section }
    ),
  acknowledgeProofOfResidence: (sessionId) =>
    api.post(
      `/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/proof-of-residence-acknowledged`
    ),
  sendApplicantHelpMessage: (sessionId, message, section) =>
    api.post(`/auth/case/${encodeURIComponent(String(sessionId || '').trim())}/help-message`, { message, section: section || undefined }),
  /** Passport/identity + DOB, sex, nationality, etc. Single PUT; `identity_doc_expiry_at` and `nie` optional. */
  updateDocumentDetails: (details) => api.put('/auth/document-details', details),
  /** AI OCR extraction from uploaded passport or identity document image/PDF. */
  analyzePassportDocument: async (file, documentType = 'passport') => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    formData.append('document_type', documentType);
    return api.post('/auth/document-details/passport-ai', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  },
  /**
   * Multi-image document scan → single PDF (`responseType` blob).
   *
   * @param {File[]} files
   * @param {boolean} [ocrEnabled=true]  Add OCR text layer (Tesseract/OCRmyPDF) on the server.
   * @param {string}  [ocrLanguages]     ``+``-joined Tesseract languages (e.g. ``"eng+spa"``).
   *                                     Defaults to backend ``OCR_LANGUAGES`` env (``"eng+spa"``).
   * @param {boolean[]|null} [skipAutoCropFlags]  One boolean per file; true skips server axis-aligned auto-crop for that page (e.g. already cropped on device).
   */
  processDocumentScan: (files, ocrEnabled = true, ocrLanguages = null, skipAutoCropFlags = null) => {
    const fd = new FormData();
    const list = files || [];
    list.forEach((f) => fd.append('images', f));
    fd.append('ocr_enabled', ocrEnabled ? 'true' : 'false');
    if (ocrLanguages) fd.append('ocr_languages', String(ocrLanguages));
    if (
      Array.isArray(skipAutoCropFlags) &&
      skipAutoCropFlags.length === list.length &&
      list.length > 0
    ) {
      fd.append('skip_auto_crop_flags', JSON.stringify(skipAutoCropFlags));
    }
    return api.post('/scan/process', fd, {
      responseType: 'blob',
      /** Launch: fail fast on weak cellular; MobileDocumentScanner shows retry / cancel modal on transport errors. */
      timeout: 10000,
      headers: { 'Content-Type': 'multipart/form-data' },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
    });
  },
  updatePersonalInfoDetails: (details) => api.put('/auth/personal-info-details', details),
  updateCivilFamilyDetails: (details) => api.put('/auth/civil-family-details', details),
  /**
   * Full address step save. Body snake_case: street_name, street_number, floor_piso, floor_door (puerta),
   * city_locality, province, postal_code, country, residence_since (YYYY-MM-01 or null).
   */
  updateAddressDetails: (details) => api.put('/auth/address-details', details),
  updateEngagementDetails: (details) => api.put('/auth/engagement-details', details),
  /**
   * Production NDA signature (participant home). Stored in Mongo personal_data (+ S3 when available).
   * Pass draft: true for stroke-end autosave; omit/false to finalize.
   */
  saveParticipantNda: (details) => api.put('/auth/participant-nda', details),
  autosaveParticipantNda: (details) =>
    api.put('/auth/applicant-autosave', {
      ndaAgreed: details?.agreed,
      ndaProductionName: details?.production_name,
      ndaSignatureDataUrl: details?.signature_data_url,
    }),
  requestProfilePhoneOtp: (phone, channel = 'sms') =>
    api.post('/auth/profile/request-phone-otp', {
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      channel,
    }),
  verifyProfilePhoneOtp: (phone, code) =>
    api.post('/auth/profile/verify-phone-otp', { phone: phone.startsWith('+') ? phone : `+${phone}`, code }),
  requestProfileEmailOtp: (email) => api.post('/auth/profile/request-email-otp', { email }),
  verifyProfileEmailOtp: (email, code) =>
    api.post('/auth/profile/verify-email-otp', { email, code }),
  getMyReferrals: () => api.get('/auth/me/referrals'),
  applyMyReferralCode: (referralCode) =>
    api.post('/auth/me/referral-code', {
      referral_code: String(referralCode || '').trim().toUpperCase(),
    }),
  deleteAccount: () => api.delete('/auth/me'),

  // --- MVP admin: registered users ---
  getAdminRegistrations: (params) => api.get('/admin/registrations', { params }),
  getAdminAdvisors: () => api.get('/admin/advisors'),
  listAdminProjects: () => api.get('/admin/projects'),
  getPortalSettings: () => api.get('/admin/portal-settings'),
  getCustomerSignupLink: (projectId) =>
    api.get('/admin/customer-signup-link', { params: { project_id: projectId } }),
  putPortalSettings: (body) => api.put('/admin/portal-settings', body),
  patchAdminUserRole: (userId, role) => api.patch(`/admin/users/${userId}/role`, { role }),
  patchAdminUserFollowUp: (userId, body) =>
    api.patch(`/admin/users/${userId}/follow-up`, body),
  patchAdminUserInquiry: (userId, body) =>
    api.patch(`/admin/users/${userId}/inquiry`, body),
  patchAdminCustomerDetails: (userId, body) =>
    api.patch(`/admin/users/${userId}/customer-details`, body),
  adminRequestPhoneOtp: (phone, channel = 'sms') =>
    api.post('/admin/users/request-phone-otp', {
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      channel,
    }),
  adminVerifyPhoneOtp: (phone, code) =>
    api.post('/admin/users/verify-phone-otp', {
      phone: phone.startsWith('+') ? phone : `+${phone}`,
      code,
    }),
  adminRequestEmailOtp: (email) => api.post('/admin/users/request-email-otp', { email }),
  adminVerifyEmailOtp: (email, code) => api.post('/admin/users/verify-email-otp', { email, code }),
  bulkAssignUnassignedCases: (advisorUserId) =>
    api.post('/admin/cases/bulk-assign-unassigned', { advisor_user_id: advisorUserId }),
  createAdminUser: (data) => api.post('/admin/users', data),
  ensureApplicantCase: (userId) => api.post(`/admin/users/${userId}/ensure-case`),
  deleteAdminUser: (userId) => api.delete(`/admin/users/${userId}`),

  // --- Session (creates case in MongoDB when user starts) ---
  startSession: (data) => api.post('/session/start', data),
  getSession: (sessionId) => api.get(`/session/${sessionId}`),
  updateLanguage: (sessionId, language) =>
    api.put(`/session/${sessionId}/language`, { language }),
  listSessions: () => api.get('/sessions'),
  rejoinSession: (sessionId) => api.post(`/session/${sessionId}/rejoin`),
  completeForm: (sessionId) => api.post(`/session/${sessionId}/complete`),
  deleteSession: (sessionId) => api.delete(`/session/${sessionId}`),
  exportFormData: (sessionId, format = 'json') =>
    api.get(`/session/${sessionId}/export`, { params: { format } }),

  // --- Chat ---
  sendMessage: (sessionId, message) => api.post(`/chat/${sessionId}`, { message }),
  getMessages: (sessionId, limit = 50) =>
    api.get(`/chat/${sessionId}/messages`, { params: { limit } }),

  /**
   * Participant Atlas - OpenAI Q&A scoped to the applicant's app + procedure.
   * @param {{ message: string, scope?: string, procedure_slug?: string, history?: Array<{role:string,content:string}> }} body
   */
  askAtlas: (body) => api.post('/atlas/ask', body, { timeout: 60000 }),

  /** Load persisted Atlas history (user/bot) for the authenticated applicant. */
  getAtlasHistory: (limit = 100) => api.get('/atlas/history', { params: { limit } }),

  // --- Document upload ---
  /**
   * Upload a case document.
   * @param {string} sessionId
   * @param {File} file
   * @param {string|null} documentType - section id (e.g. passport_id)
   * @param {{
   *  caseOwnerUserId?: number|string,
   *  coverageDateFrom?: string,
   *  coverageDateTo?: string,
   *  residencyDocLabel?: string,
   *  selfEmploymentActivity?: string,
   *  supportingDescription?: string,
   *  criminalRecordCountry?: string
   * }} [opts] - Upload metadata; `caseOwnerUserId` is admin-only.
   */
  uploadDocument: async (sessionId, file, documentType = null, opts = {}) => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    if (documentType) formData.append('document_type', documentType);
    if (opts.coverageDateFrom) formData.append('coverage_date_from', String(opts.coverageDateFrom));
    if (opts.coverageDateTo) formData.append('coverage_date_to', String(opts.coverageDateTo));
    if (opts.residencyDocLabel) formData.append('residency_doc_label', String(opts.residencyDocLabel));
    if (opts.selfEmploymentActivity) {
      formData.append('self_employment_activity', String(opts.selfEmploymentActivity).slice(0, 100));
    }
    if (opts.supportingDescription) {
      formData.append('supporting_description', String(opts.supportingDescription).slice(0, 500));
    }
    if (opts.criminalRecordCountry) {
      formData.append('criminal_record_country', String(opts.criminalRecordCountry).slice(0, 100));
    }
    if (opts.caseOwnerUserId != null && opts.caseOwnerUserId !== '') {
      formData.append('case_owner_user_id', String(opts.caseOwnerUserId));
    }
    return api.post(`/upload/${sessionId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /**
   * Admin: explicit REST path (same behavior as uploadDocument + caseOwnerUserId).
   * @param {{
   *  coverageDateFrom?: string,
   *  coverageDateTo?: string,
   *  residencyDocLabel?: string,
   *  selfEmploymentActivity?: string,
   *  supportingDescription?: string,
   *  criminalRecordCountry?: string
   * }} [opts]
   */
  uploadAdminCaseDocument: async (userId, sessionId, file, documentType = null, opts = {}) => {
    const prep = await prepareDocumentUploadFile(file);
    if (!prep.ok) return rejectUploadPrepare(prep.message);
    const formData = new FormData();
    formData.append('file', prep.file);
    if (documentType) formData.append('document_type', documentType);
    if (opts.coverageDateFrom) formData.append('coverage_date_from', String(opts.coverageDateFrom));
    if (opts.coverageDateTo) formData.append('coverage_date_to', String(opts.coverageDateTo));
    if (opts.residencyDocLabel) formData.append('residency_doc_label', String(opts.residencyDocLabel));
    if (opts.selfEmploymentActivity) {
      formData.append('self_employment_activity', String(opts.selfEmploymentActivity).slice(0, 100));
    }
    if (opts.supportingDescription) {
      formData.append('supporting_description', String(opts.supportingDescription).slice(0, 500));
    }
    if (opts.criminalRecordCountry) {
      formData.append('criminal_record_country', String(opts.criminalRecordCountry).slice(0, 100));
    }
    return api.post(`/admin/cases/${userId}/${sessionId}/documents/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  /** Customer: presigned download URL for own session file (S3 only). */
  getDocumentDownloadUrl: (sessionId, documentId) =>
    api.get(`/documents/${sessionId}/${documentId}/download-url`),
  /** Admin: fresh presigned URL for any case file. */
  getAdminDocumentDownloadUrl: (userId, sessionId, documentId) =>
    api.get(`/admin/cases/${userId}/${sessionId}/documents/${documentId}/download-url`),
  /** Admin: stream file through API (JWT); avoids S3 presigned SignatureDoesNotMatch in browser. */
  downloadAdminCaseDocumentFile: (userId, sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.get(`/admin/cases/${userId}/${sessionId}/documents/${id}/file`, {
      responseType: 'blob',
    });
  },
  /** Owner: stream file through API (JWT). */
  downloadSessionDocumentFile: (sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.get(`/documents/${sessionId}/${id}/file`, { responseType: 'blob' });
  },
  /** Owner: list session uploads (ids + section slots) for checklist sync after reload. */
  getSessionDocumentsSummary: (sessionId) =>
    api.get(`/documents/session/${sessionId}/summary`),
  updateSessionDocumentMetadata: (sessionId, documentId, payload) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.put(`/documents/${sessionId}/${id}/metadata`, payload);
  },
  /** Owner: hard-delete one uploaded document (Mongo + storage). */
  deleteSessionDocument: (sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.delete(`/documents/${sessionId}/${id}`);
  },

  // --- Data ---
  getCollectedData: (sessionId, formType = null) =>
    api.get(`/data/${sessionId}`, formType ? { params: { form_type: formType } } : undefined),
  exportData: (sessionId, format = 'json') =>
    api.get(`/data/${sessionId}/export`, { params: { format } }),
  updateField: (sessionId, field, value) =>
    api.put(`/data/${sessionId}/field`, { field, value }),

  // --- Forms ---
  getFormTypes: (language) => api.get('/forms', { params: { language } }),
  getFormFields: (formId, language) =>
    api.get(`/forms/${formId}/fields`, { params: { language } }),
  getFormHTML: (sessionId) =>
    api.get(`/session/${sessionId}/form/html`, { responseType: 'blob' }),

  // --- Live chat (AI + human handoff) ---
  startLiveChatSession: () => api.post('/livechat/session/start'),
  getLiveChatSession: (sessionId) => api.get(`/livechat/session/${sessionId}`),
  getLiveChatMessages: (sessionId) => api.get(`/livechat/session/${sessionId}/messages`),
  sendLiveChatMessage: (sessionId, content) =>
    api.post(`/livechat/session/${sessionId}/messages`, { content }),
  requestLiveChatHandoff: (sessionId, reason = null) =>
    api.post(`/livechat/session/${sessionId}/handoff`, { reason }),
  getLiveChatQueueStatus: (sessionId) =>
    api.get(`/livechat/session/${sessionId}/queue`),
  closeLiveChatSession: (sessionId) =>
    api.post(`/livechat/session/${sessionId}/close`),
  checkAgentAvailability: (signal) =>
    api.get('/livechat/availability/check', signal ? { signal } : {}),

  // --- Agent (live chat) ---
  getAgentAvailability: () => api.get('/livechat/agent/availability'),
  setAgentAvailability: (status, capacity = 1) =>
    api.post('/livechat/agent/availability', { status, capacity }),
  getAgentQueue: () => api.get('/livechat/agent/queue'),
  assignAgentSession: (sessionId) => api.post(`/livechat/agent/assign/${sessionId}`),
  getAgentActiveChats: () => api.get('/livechat/agent/active'),
  sendAgentMessage: (sessionId, content) =>
    api.post(`/livechat/agent/session/${sessionId}/messages`, { content }),
  closeAgentSession: (sessionId) =>
    api.post(`/livechat/agent/session/${sessionId}/close`),

  // --- Admin: cases (MongoDB sessions) ---
  getAllUserCases: (params) => api.get('/admin/cases', { params, timeout: 90000 }),
  getCaseByPublicId: (publicCaseId) =>
    api.get(`/admin/cases/by-public-id/${encodeURIComponent(String(publicCaseId || '').trim())}`),
  getUserCaseDetail: (userId, sessionId) =>
    api.get(`/admin/cases/${userId}/${sessionId}`),
  /** Admin: assign or unassign advisor on one case (`advisorUserId` null clears). */
  patchCaseAssignment: (userId, sessionId, body) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/assignment`, body),
  patchCaseNeedsAttention: (userId, sessionId, body) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/needs-attention`, body),
  getCasesAlertsSummary: (projectId) =>
    api.get('/admin/cases/alerts-summary', { params: projectId != null ? { project_id: projectId } : {} }),
  /** Advisor metadata patch (referral, prior asylum, fee, workflow status). */
  patchCaseExtensions: (userId, sessionId, body) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/case-extensions`, body),
  patchCaseStatus: (userId, sessionId, statusValue) =>
    api.patch(`/admin/cases/${userId}/${sessionId}/case-extensions`, { case_status: statusValue }),
  lookupReferralCode: (code) =>
    api.get('/admin/referrals/lookup', { params: { code: String(code || '').trim() } }),
  lookupPartnerCode: (code) =>
    api.get('/admin/partners/lookup', { params: { code: String(code || '').trim() } }),
  lookupFeeCampaign: (code) =>
    api.get(`/admin/fee-campaigns/${encodeURIComponent(String(code || '').trim())}`),
  getCaseHistory: (userId, sessionId, config) =>
    api.get(`/admin/cases/${userId}/${sessionId}/history`, config),
  postCaseHistoryNote: (userId, sessionId, body) =>
    api.post(`/admin/cases/${userId}/${sessionId}/history/note`, body),
  markHelpMessageResponded: (userId, sessionId, messageId, replyText) =>
    api.post(`/admin/cases/${userId}/${sessionId}/history/help-responded/${messageId}`, {
      text: replyText ? String(replyText) : '',
    }),
  markFamilyInterestResponded: (userId, sessionId, messageId, replyText) =>
    api.post(`/admin/cases/${userId}/${sessionId}/history/family-interest-responded/${messageId}`, {
      text: replyText ? String(replyText) : '',
    }),
  /** Customer-level advisor timeline (not tied to one case session). */
  getUserProfileHistory: (userId, config) => api.get(`/admin/users/${userId}/history`, config),
  postUserProfileHistoryNote: (userId, body) =>
    api.post(`/admin/users/${userId}/history/note`, body),
  updateCaseFields: (userId, sessionId, fields) =>
    api.put(`/admin/cases/${userId}/${sessionId}/fields`, { fields }),
  getAdminExFormPreview: (userId, sessionId, formCode, download = false) =>
    api.get(`/admin/cases/${userId}/${sessionId}/forms/ex-preview`, {
      params: {
        form_code: formCode || undefined,
        download: download ? 'true' : 'false',
      },
      responseType: 'blob',
    }),
  autosaveAdminCaseDocumentDetails: (userId, sessionId, fields) =>
    api.put(`/admin/cases/${userId}/${sessionId}/documents/autosave`, { fields }),
  requestDocumentReupload: (userId, sessionId, documentType, reason) =>
    api.post(`/admin/cases/${userId}/${sessionId}/request-reupload`, {
      document_type: documentType,
      reason,
    }),
  updateAdminDocumentStatus: (userId, sessionId, documentId, statusValue, reason = null) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.put(`/admin/cases/${userId}/${sessionId}/documents/${id}/status`, {
      status: statusValue,
      reason: reason || undefined,
    });
  },
  deleteAdminDocument: (userId, sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.delete(`/admin/cases/${userId}/${sessionId}/documents/${id}`);
  },
  restoreAdminDocument: (userId, sessionId, documentId) => {
    const id = encodeURIComponent(String(documentId ?? '').trim());
    return api.post(`/admin/cases/${userId}/${sessionId}/documents/${id}/restore`);
  },

  // --- Admin: family groups ---
  createFamilyGroup: (familyName, leadContactUserId) =>
    api.post('/admin/family-groups', {
      family_name: familyName,
      lead_contact_user_id: leadContactUserId,
    }),
  getFamilyGroupByUser: (userId) =>
    api.get(`/admin/family-groups/by-user/${userId}`),
  getFamilyMembers: (familyGroupId) =>
    api.get(`/admin/family-groups/${familyGroupId}/members`),
  addFamilyMember: (familyGroupId, userId, relationshipToLead = null) =>
    api.post(`/admin/family-groups/${familyGroupId}/members`, {
      user_id: userId,
      relationship_to_lead: relationshipToLead || undefined,
    }),
};

// ---------------------------------------------------------------------------
// Org-auth API (public - no JWT required)
// ---------------------------------------------------------------------------

export const orgAuthApi = {
  /** [Legacy] Validate a one-time invite token; returns org name + prefilled email */
  validateInvite: (token) =>
    api.get(`/org-auth/invite/${encodeURIComponent(token)}`),

  /** Send SMS OTP for org-admin signup phone verification */
  requestPhoneOtp: (phone) =>
    api.post('/org-auth/phone/request-otp', { phone }, { public: true }),

  /** Verify SMS OTP; returns phone_otp_token for signup */
  verifyPhoneOtp: (phone, code) =>
    api.post('/org-auth/phone/verify-otp', { phone, code }, { public: true }),

  /** Complete org-admin registration (ref= primary, invite_token= legacy) */
  signup: (payload) =>
    api.post('/org-auth/signup', payload),

  /** List productions for the authenticated org */
  listProjects: () =>
    api.get('/org-auth/projects'),

  /** Production detail with participant roster */
  getProduction: (productionId) =>
    api.get(`/org-auth/projects/${productionId}`),

  /** Single participant within a production */
  getProductionParticipant: (productionId, participantId) =>
    api.get(`/org-auth/projects/${productionId}/participants/${participantId}`),

  /** Crew who have not registered yet (invitation banner list) */
  getNotRegisteredParticipants: (productionId) =>
    api.get(`/org-auth/projects/${productionId}/participants/not-registered`),

  /** Production tasks preview (NDAs, itineraries, acknowledgements) */
  getProductionTasks: (productionId) =>
    api.get(`/org-auth/projects/${productionId}/tasks`),

  updateProduction: (productionId, payload) =>
    api.patch(`/org-auth/projects/${productionId}`, payload),

  closeProduction: (productionId) =>
    api.post(`/org-auth/projects/${productionId}/close`),

  deleteProduction: (productionId) =>
    api.delete(`/org-auth/projects/${productionId}`),

  /** Soft-reset participants on a production (unregister + wipe filled data).
   *  Can take a while on large rosters (Mongo wipe + re-seed per person). */
  resetProduction: (productionId) =>
    api.post(`/org-auth/projects/${productionId}/reset`, null, { timeout: 180000 }),

  /** Enable a production toolkit request (persists; shows In progress with live counts). */
  enableProductionRequest: (productionId, slug) =>
    api.post(
      `/org-auth/projects/${productionId}/requests/${encodeURIComponent(String(slug).trim())}/enable`,
    ),
  /** Read persisted production widget config. */
  getProductionRequestsConfig: (productionId) =>
    api.get(`/org-auth/projects/${productionId}/requests-config`),
  /** Persist production widget config. */
  saveProductionRequestsConfig: (productionId, enabledRequestSlugs) =>
    api.put(`/org-auth/projects/${productionId}/requests-config`, {
      enabled_request_slugs: enabledRequestSlugs,
    }),

  /** Email invite links to all roster people who have not been emailed yet. */
  sendProductionInvitations: (productionId) =>
    api.post(`/org-auth/projects/${productionId}/send-invitations`, null, { timeout: 120000 }),

  /** Re-email one crew invite magic link. */
  resendProductionInvite: (productionId, participantId) =>
    api.post(`/org-auth/projects/${productionId}/participants/${participantId}/resend-invite`),

  /** List Roster / Budget / Schedule / DOOD sample files for this org */
  getSamplePackInfo: () =>
    api.get('/org-auth/projects/admin-tools/sample-pack'),

  /** Download all four sample CSVs as a zip */
  downloadSamplePackZip: () =>
    api.get('/org-auth/projects/admin-tools/sample-pack/zip', { responseType: 'blob' }),

  /** Download one sample CSV: roster | budget | schedule | day_out_of_days */
  downloadSamplePackFile: (kind) =>
    api.get(`/org-auth/projects/admin-tools/sample-pack/${encodeURIComponent(kind)}`, {
      responseType: 'blob',
    }),

  /** Generate + persist Roster/Budget/Schedule/DOOD from production parameters */
  generateProductionPack: (body) =>
    api.post('/org-auth/projects/admin-tools/generate-pack', body),

  /** List previously generated (persistent) packs for this org */
  listGeneratedPacks: () =>
    api.get('/org-auth/projects/admin-tools/generated-packs'),

  downloadGeneratedPackFile: (packId, kind) =>
    api.get(
      `/org-auth/projects/admin-tools/generated-packs/${encodeURIComponent(packId)}/files/${encodeURIComponent(kind)}`,
      { responseType: 'blob' },
    ),

  downloadGeneratedPackZip: (packId) =>
    api.get(
      `/org-auth/projects/admin-tools/generated-packs/${encodeURIComponent(packId)}/zip`,
      { responseType: 'blob' },
    ),

  /** Sample travel itinerary CSV for one roster person (nationality → shoot country) */
  downloadParticipantTravelItinerary: (productionId, participantId) =>
    api.get(
      `/org-auth/projects/${productionId}/participants/${participantId}/sample-travel-itinerary`,
      { responseType: 'blob' },
    ),

  /** Create a mobility project for the authenticated org */
  createProject: (payload) =>
    api.post('/org-auth/projects', payload),

  /** Upload crew + Schedule / Day out of days / Budget CSVs for an existing project */
  importCrewAndInvite: (projectId, crewFile, extras = {}) => {
    const form = new FormData();
    form.append('file', crewFile);
    if (extras.scheduleFile) form.append('schedule_file', extras.scheduleFile);
    if (extras.dayOutOfDaysFile) form.append('day_out_of_days_file', extras.dayOutOfDaysFile);
    if (extras.budgetFile) form.append('budget_file', extras.budgetFile);
    return api.post(`/org-auth/projects/${projectId}/crew/import-and-invite`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** Create project from crew CSV; store Schedule / Day out of days / Budget in S3 + Mongo */
  importCrewAndInviteNewProject: (crewFile, extras = {}) => {
    const form = new FormData();
    form.append('file', crewFile);
    if (extras.scheduleFile) form.append('schedule_file', extras.scheduleFile);
    if (extras.dayOutOfDaysFile) form.append('day_out_of_days_file', extras.dayOutOfDaysFile);
    if (extras.budgetFile) form.append('budget_file', extras.budgetFile);
    return api.post('/org-auth/projects/crew/import-and-invite', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /** List stored production attachments (Schedule / Day out of days / Budget) */
  listProductionAttachments: (productionId) =>
    api.get(`/org-auth/projects/${productionId}/attachments`),

  /** Consume a reset token and set a new password (legacy accounts only) */
  resetPassword: (token, newPassword) =>
    api.post('/org-auth/reset-password', { token, new_password: newPassword }),

  /** List users in the authenticated org (search by name, email, phone, id) */
  listOrganizationUsers: (params = {}) =>
    api.get('/organizations/users', { params }),

  /**
   * Set or clear a member's email and/or phone.
   * Pass empty string for email/phone to clear that credential.
   */
  updateOrganizationUserContact: (userId, body) =>
    api.patch(`/organizations/users/${userId}/contact`, body),

  /** Update roster invite email/phone (works before registration). */
  updateParticipantContact: (productionId, participantId, body) =>
    api.patch(
      `/org-auth/projects/${productionId}/participants/${participantId}/contact`,
      body,
    ),

  /** Add one crew member to a production roster. */
  addProductionParticipant: (productionId, body) =>
    api.post(`/org-auth/projects/${productionId}/participants`, body),

  /** Remove one crew member from a production roster. */
  deleteProductionParticipant: (productionId, participantId) =>
    api.delete(`/org-auth/projects/${productionId}/participants/${participantId}`),
};

// ---------------------------------------------------------------------------
// Super-admin invite API (JWT required - super_admin role)
// ---------------------------------------------------------------------------

export const superAdminInviteApi = {
  /** Create an invite for an org admin */
  createInvite: (organizationId, email = null, ttlHours = null) =>
    api.post(`/super-admin/organizations/${organizationId}/invites`, {
      email: email || undefined,
      ttl_hours: ttlHours || undefined,
    }),

  /** List invites for an org */
  listInvites: (organizationId, { includeExpired = false, includeUsed = false } = {}) =>
    api.get(`/super-admin/organizations/${organizationId}/invites`, {
      params: {
        include_expired: includeExpired,
        include_used: includeUsed,
      },
    }),

  /** Revoke an invite */
  revokeInvite: (organizationId, token) =>
    api.delete(`/super-admin/organizations/${organizationId}/invites/${encodeURIComponent(token)}`),
};

export const API_BASE_URL = getApiBaseUrl();

export function getGoogleAuthUrl(intent = 'signup', partnerToken = null, referralCode = null) {
  const origin = getBackendOrigin();
  const params = new URLSearchParams();
  params.set('intent', intent);
  // Always show the Google account picker instead of silently reusing the active session.
  params.set('force_account_select', '1');
  if (partnerToken) params.set('partner_token', partnerToken);
  if (referralCode) params.set('referral_code', referralCode);
  return `${origin}/api/auth/google?${params.toString()}`;
}

export function getAppleAuthUrl(intent = 'signup', partnerToken = null, referralCode = null) {
  const origin = getBackendOrigin();
  const params = new URLSearchParams();
  params.set('intent', intent);
  // Ask backend to force Apple re-auth on login so stale provider sessions are not auto-reused.
  if (intent === 'login') params.set('force_account_select', '1');
  if (partnerToken) params.set('partner_token', partnerToken);
  if (referralCode) params.set('referral_code', referralCode);
  return `${origin}/api/auth/apple?${params.toString()}`;
}
